import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { showChat } from '../../../../../utils/liveChat';
import Badge from '../../../../common/base/Badge';
import Button, { ClickableArea } from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import CopyableField from '../../../../common/CopyableField';
import { Div } from '../../../../common/helpers/StyledUtils';
import { DashboardContext } from '../../DashboardContext';
import { CLIOnboardingContext } from '../GuidedCLIOnboarding';
interface Props {
  context: CLIOnboardingContext;
  nextStep: () => void;
  is_current_step: boolean;
}
const StyledCardStatus = styled(StyledCard)<{
  connected: boolean;
}>(({
  theme
}) => css`
    background:
      linear-gradient(
        90deg,
        ${theme.colors.surface.base.surface} 0%,
        ${theme.colors.surface.base.surface}80 100%
      ),
      url(${`/images/background_lines_${theme.mode}.svg`}) no-repeat center center;
  `);
const ConnectCLI: React.FC<Props> = t0 => {
  const $ = _c(49);
  const {
    context,
    nextStep
  } = t0;
  const {
    source,
    cli_connected
  } = context;
  const [show_output, setShowOutput] = useState(false);
  const {
    team
  } = useContext(DashboardContext);
  const connect_command = source ? `hookdeck listen 3000 ${source.name} --path /webhooks` : "hookdeck listen 3000 source-name --path /webhooks";
  let t1;
  if ($[0] !== nextStep) {
    t1 = () => {
      nextStep();
    };
    $[0] = nextStep;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNext = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <StyledCardSection p={3}><Icon icon="info" muted={true} small={true} left={true} />Run the below in a new terminal window, and do not close it.</StyledCardSection>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      x: 3,
      y: 3,
      r: 4.5
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== connect_command) {
    t4 = <StyledCard overflow_hidden={true}>{t2}<StyledCardSection><Div data-testid="guided-cli-connect-command"><CopyableField value={connect_command} mono={true} variant="command_list" borderless={true} p={t3} /></Div></StyledCardSection></StyledCard>;
    $[4] = connect_command;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      t: 4
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      y: 0,
      x: 1
    };
    t7 = {
      l: -1
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      width: "auto"
    };
    t9 = {
      justify: "flex-start",
      align: "center",
      gap: 1
    };
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  let t10;
  if ($[11] !== show_output) {
    t10 = () => setShowOutput(!show_output);
    $[11] = show_output;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const t11 = !show_output;
  let t12;
  if ($[13] !== t11) {
    t12 = <Text size="xs" semi={true} muted={t11}>View intended output</Text>;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = show_output ? "arrow_drop_down" : "arrow_right";
  let t14;
  if ($[15] !== t13) {
    t14 = <Icon muted={true} icon={t13} />;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t10 || $[18] !== t12 || $[19] !== t14) {
    t15 = <Div m={t5}><ClickableArea p={t6} m={t7} rounded={true} style={t8} flex={t9} onClick={t10}>{t12}{t14}</ClickableArea></Div>;
    $[17] = t10;
    $[18] = t12;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== show_output || $[22] !== source?.id || $[23] !== source?.name || $[24] !== team?.id) {
    t16 = show_output && <StyledCard overflow_hidden={true} m={{
      t: 2
    }} borderless={false}><StyledCardSection muted={true} p={3} style={{
        overflowX: "scroll",
        whiteSpace: "nowrap"
      }}><Text mono={true} size="s">●── HOOKDECK CLI ──●<br /><br />Listening on 1 source • 1 connection • [i] Collapse<br /><br />{source?.name}<br />│{"  "}Requests to → https://hkdk.events/{source?.id}<br />└─ Forwards to → http://localhost:3030/webhooks (cli-http)<br /><br />{"\uD83D\uDCA1 View dashboard to inspect, retry & bookmark events: https://dashboard.hookdeck.com/events/cli?team_id="}{team?.id}<br /><br />Events • [↑↓] Navigate ────────────────────────────────────────────────────────────────────────────────────────────────<br /><br />○ Connected. Waiting for events...</Text></StyledCardSection></StyledCard>;
    $[21] = show_output;
    $[22] = source?.id;
    $[23] = source?.name;
    $[24] = team?.id;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      t: 6
    };
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      justify: "space-between",
      align: "center",
      gap: 2
    };
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      align: "center"
    };
    t20 = <Text semi={true}>CLI Status</Text>;
    $[28] = t19;
    $[29] = t20;
  } else {
    t19 = $[28];
    t20 = $[29];
  }
  let t21;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      label: "My local server isn't connecting to the CLI",
      icon: "help",
      onClick: _temp
    };
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Div flex={t19}>{t20}<Tooltip tooltip="When you successfully connect your local server to the Hookdeck CLI, the badge to the right will indicate 'Connected,' and you'll be able to proceed to the next step." cta={t21} max_width={380}><Icon icon="info" muted={true} right={true} pointer={true} /></Tooltip></Div>;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== cli_connected) {
    t23 = cli_connected ? <Badge success={true} subtle={true} icon="done_all" data-testid="guided-cli-connected-badge">Connected</Badge> : <Badge neutral={true} icon="disconnect">Not connected</Badge>;
    $[32] = cli_connected;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== cli_connected || $[35] !== t23) {
    t24 = <Div m={t17}><StyledCardStatus data-testid="guided-cli-connect-status" p={3} connected={cli_connected} flex={t18}>{t22}{t23}</StyledCardStatus></Div>;
    $[34] = cli_connected;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      t: 8
    };
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = cli_connected && context.progress <= 2;
  const t27 = !cli_connected || context.progress > 2;
  const t28 = !cli_connected;
  let t29;
  if ($[38] !== handleNext || $[39] !== t26 || $[40] !== t27 || $[41] !== t28) {
    t29 = <Div m={t25}><Button data-testid="guided-cli-connect-next" primary={t26} outline={t27} onClick={handleNext} disabled={t28}>Next</Button></Div>;
    $[38] = handleNext;
    $[39] = t26;
    $[40] = t27;
    $[41] = t28;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t24 || $[46] !== t29 || $[47] !== t4) {
    t30 = <Div>{t4}{t15}{t16}{t24}{t29}</Div>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t24;
    $[46] = t29;
    $[47] = t4;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  return t30;
};
export default ConnectCLI;
function _temp() {
  showChat();
}